import React, { CSSProperties, PureComponent, ReactElement, ReactNode } from 'react';
import AppContext from '../../common/context/AppContext';
import { ShowModalEvent } from '../../common/eventbus/Events';
import { Modal } from 'antd';
import { Rnd } from 'react-rnd';
import Utils from '../../common/util/Utils';

export interface ModalPanel {
    content: ReactElement;
    width: string;
    left?: number | string;
    top?: number | string;
    closable: boolean;
}

export interface ModalPanelProps {
    onClose?: () => void;
    onWidthChange?: (modalWidth: string) => void;
}

interface DashboardModalProps {}

interface DashboardModalState {
    modalPanels: Array<ModalPanel>;
}

const DEFAULT_MODAL_WIDTH = '70em';

export class DashboardModal extends PureComponent<DashboardModalProps, DashboardModalState> {
    state = {
        modalPanels: [],
    };
    constructor(props) {
        super(props);
        this.registerEvents();
    }

    registerEvents = () => {
        AppContext.eventBus.register(ShowModalEvent.NAME, (event: ShowModalEvent) => {
            this.addModalPanel({
                content: event.content,
                width: event.width,
                top: event.positionTop,
                left: event.positionLeft,
                // By default, modals are closable with an outside click
                closable: Utils.isNotExists(event.closable) ? true : event.closable,
            });
        });
    };
    addModalPanel = (modalPanel: ModalPanel) => {
        // Set callbacks to the modal inner component that allow it to control closing/resizing the modal panel
        modalPanel.content = React.cloneElement(modalPanel.content, {
            onClose: this.onClose,
            onWidthChange: this.handleWidthChange,
        });

        // Use last up to date state to make the new state
        this.setState(prevSate => {
            const updated = prevSate.modalPanels.slice();
            updated.push(modalPanel);
            return {
                modalPanels: updated,
            };
        });
    };
    onClose = () => {
        this.setState(prevSate => {
            const updated = prevSate.modalPanels.slice();
            updated.splice(updated.length - 1, 1);
            return {
                modalPanels: updated,
            };
        });
    };
    handleWidthChange = (modalWidth: string) => {
        const updated = this.state.modalPanels.slice();
        const lastPanel = this.state.modalPanels[updated.length - 1];
        const updatedPanel = Object.assign({}, lastPanel, { width: modalWidth });
        updated.splice(updated.length - 1, 1, updatedPanel);
        this.setState({ modalPanels: updated });
    };
    render() {
        if (!this.state.modalPanels?.length) return null;

        return (
            <div onContextMenu={this.handleModalContextMenu}>
                {this.state.modalPanels.map((modalPanel: ModalPanel, idx) => {
                    return (
                        <Modal
                            key={`modal-panel-#${idx}`}
                            mask={true}
                            open={true}
                            width={modalPanel.width || DEFAULT_MODAL_WIDTH}
                            maskClosable={modalPanel.closable}
                            maskStyle={{ backgroundColor: 'rgba(0,0,0,0.2)' }}
                            modalRender={(modal: ReactNode) => (
                                <Rnd
                                    dragHandleClassName="modal-title"
                                    style={{ position: 'relative' }}
                                    size={{ width: '100%', height: '100%' }}>
                                    {modal}
                                </Rnd>
                            )}
                            onCancel={this.onClose}
                            centered={this.isCentered(modalPanel)}
                            footer={null}
                            style={this.getStyle(modalPanel)}>
                            {modalPanel.content}
                        </Modal>
                    );
                })}
            </div>
        );
    }

    private isCentered(modalPanel: ModalPanel) {
        return Utils.isNotExists(modalPanel.top) && Utils.isNotExists(modalPanel.left);
    }

    private getStyle(modalPanel: ModalPanel): CSSProperties {
        if (Utils.isNotExists(modalPanel.top) && Utils.isNotExists(modalPanel.left)) return null;

        const top = typeof modalPanel.top === 'string' ? modalPanel.top : `${modalPanel.top}px`;
        const left = typeof modalPanel.left === 'string' ? modalPanel.left : `${modalPanel.left}px`;

        return {
            top,
            left,
            margin: 'unset',
        };
    }

    /**
     * Added this event handler to avoid showing OS context menu when the user right clicks outside the modal content.
     * @param event
     */
    handleModalContextMenu = event => {
        event.preventDefault();
    };
}
